import React, { useEffect, useState } from 'react';
import { compose, graphql } from 'react-apollo';

import Link from 'components/Link';
import LoadingIndicator from 'components/LoadingIndicator';
import {
  AdminAddContactInfoToUserInput,
  AdminAddContactInfoConfirmationActionEnum,
} from 'globalTypes';

import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { safePromise } from 'utils/tsUtils';

import { apiContactInfoForUser, apiContactInfoForUser_api_contactInfoForUser } from './__generated__/apiContactInfoForUser';
import { apiAdminAddContactInfoToUser } from './__generated__/apiAdminAddContactInfoToUser';
import { userProfileScreen } from './__generated__/userProfileScreen';
import {
  apiAdminGetContactInfoForDevice,
  apiAdminGetContactInfoForDeviceVariables,
  apiAdminGetContactInfoForDevice_api_adminGetContactInfoForDevice,
} from './__generated__/apiAdminGetContactInfoForDevice';

import API_CONTACT_INFO_FOR_USER from './apiContactInfoForUser.gql';
import API_ADMIN_ADD_CONTACT_INFO_TO_USER from './apiAdminAddContactInfoToUser.gql';
import API_ADMIN_GET_CONTACT_INFO_FOR_DEVICE from './apiAdminGetContactInfoForDevice.gql';
import ContactInfoDialog from './ContactInfoDialog';
import RosteredDeviceDialog from './RosteredDeviceModal/RosteredDeviceDialog';
import ContactMergeDialog from './ContactMergeDialog';
import USER_PROFILE_SCREEN from './userProfileScreen.gql';

const PROMPT_ERROR_CODES = ["prompt_merge", "device_can_be_stolen", "device_can_be_taken", "prompt_merge_or_steal"]

type CompletedAction = 'added' | 'merged' | 'removed' | 'updated';

export type ContactInfoProps = {
  relatedUserUuid: string;
  organizationUuid: string;
  onComplete: (value: CompletedAction) => void;
  onError: (errorMessage: string) => void;
  onHide: () => void;
  selectedDevice?: apiContactInfoForUser_api_contactInfoForUser;
};

function parseGroupNames(groupNames: string[]) {
  if (groupNames.length <= 3) {
    return groupNames.join(', ');
  }

  return `${groupNames.slice(0, 3).join(', ')} and others ${groupNames.length - 3}`;
}

const ContactInfo = (params: ContactInfoProps): JSX.Element => {
  const props = params as ContactInfoProps & {
    userProfileScreen: { loading: boolean; error: Error } & userProfileScreen;
    contactInfoForUser: { loading: boolean; error: Error } & apiContactInfoForUser;
    addContactInfo: (value: {
      variables: { input: AdminAddContactInfoToUserInput };
    }) => Promise<{ data: apiAdminAddContactInfoToUser }>;
  };

  const {
    loading: loadingUserProfileScreen,
    error: errorUserProfileScreen,
    userProfileScreen,
  } = props.userProfileScreen;
  const {
    loading: loadingContactInfoForUser,
    error: errorContactInfoForUser,
    api_contactInfoForUser,
  } = props.contactInfoForUser;

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showRosteredEdit, setShowRosteredEdit] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [dataContactInfo, setDataContactInfo] = useState<{
    deviceUid: string;
    isRostered: boolean;
    contactInfoForDevice?: apiAdminGetContactInfoForDevice_api_adminGetContactInfoForDevice;
    originalAction: CompletedAction
  } | null>(null);

  useEffect(() => {
    // Log it to rollbar
    if (errorUserProfileScreen) {
      console.error(errorUserProfileScreen);
    }
  }, [errorUserProfileScreen]);

  useEffect(() => {
    // Log it to rollbar
    if (errorContactInfoForUser) {
      console.error(errorContactInfoForUser);
    }
  }, [errorContactInfoForUser]);

  useEffect(() => {
    if (props.selectedDevice) {
      if (props.selectedDevice.rostered) {
        setShowRosteredEdit(true);
      } else {
        setShowEdit(true);
      }
    } else {
      setShowEdit(false);
    }
  }, [props.selectedDevice]);

  if (loadingUserProfileScreen || loadingContactInfoForUser) {
    return <LoadingIndicator />;
  }

  if (
    errorUserProfileScreen ||
    errorContactInfoForUser ||
    !userProfileScreen ||
    !userProfileScreen.canAddDevice
  ) {
    return <span />;
  }

  const submitAddDevice = async ({
    deviceUid,
    confirmAction,
    action,
  }: {
    deviceUid: string;
    confirmAction?: AdminAddContactInfoConfirmationActionEnum | null;
    action: CompletedAction;
  }): Promise<{ data: apiAdminAddContactInfoToUser} | null> => {
    setMutationLoading(true);
    const [result, error] = await safePromise(
      props.addContactInfo({
        variables: {
          input: {
            deviceUid,
            targetUserUuid: props.relatedUserUuid,
            organizationUuid: props.organizationUuid,
            confirmAction,
            deviceUidToRemove: getSelectedDeviceUid() || ""
          },
        },
      })
    );
    setMutationLoading(false);

    if (error) {
      props.onError(error.message);
      return null;
    }

    if (result?.data?.api_adminAddContactInfoToUser?.success) {
      setShowAdd(false);
      setShowEdit(false);
      setDataContactInfo(null);
      props.onComplete(action);
      return result;
    }

    // return result to component specific behavior
    return result;
  };

  const handleEditAddDevice = async (value:string, action: CompletedAction): Promise<{ data: apiAdminAddContactInfoToUser} | null> => {
    const result = await submitAddDevice({
      deviceUid: value,
      action: action,
    });

    if (result?.data?.api_adminAddContactInfoToUser?.errors) {
      const prompt = result.data.api_adminAddContactInfoToUser.errors.find((e: any) =>
        PROMPT_ERROR_CODES.includes(e.code)
      );

      const apollo = getApolloClientSingleton();

      if (prompt) {
        const contactDevice = await apollo.query<
          apiAdminGetContactInfoForDevice,
          apiAdminGetContactInfoForDeviceVariables
        >({
          query: API_ADMIN_GET_CONTACT_INFO_FOR_DEVICE,
          variables: {
            deviceUid: value,
          },
        });

        setShowAdd(false);
        setShowEdit(false);
        setDataContactInfo({
          deviceUid: value,
          isRostered: Boolean(
            result.data.api_adminAddContactInfoToUser.errors.find(
              (e: any) => e.code === 'prompt_merge'
            )
          ),
          contactInfoForDevice: contactDevice.data.api_adminGetContactInfoForDevice,
          originalAction: action
        });
      } else {
        props.onError(result.data.api_adminAddContactInfoToUser.errors[0].message);
      }
    }

    return result;
  };
  
  const handleAddDevice = async (value:string) => {
    return handleEditAddDevice(value, 'added');
  };

  const handleEditDevice = async (value:string) => {
    return handleEditAddDevice(value, 'updated');
  };

  const handleAddContactInfo = () => {
    setShowEdit(false);
    setShowAdd(true);
  };

  const isEditing = () => {
    if (showEdit && props.selectedDevice) {
      return true;
    }
    return false;
  }

  const getSelectedDeviceUUid = () => {
    return props.selectedDevice?.uuid ? props.selectedDevice?.uuid : undefined;
  };
  
  const getSelectedDeviceUid = () => {
    return props.selectedDevice?.uid ? props.selectedDevice?.uid : undefined;
  };

  return (
    <>
      {(showAdd || showEdit) && (
        <ContactInfoDialog
          deviceUuid={getSelectedDeviceUUid()}
          deviceUid={getSelectedDeviceUid()}
          name={userProfileScreen.identification.fullName}
          currentDevices={api_contactInfoForUser?.map(contactInfo => contactInfo.uid) ?? []}
          onRequestHide={() => {
            setShowAdd(false);
            setShowEdit(false);
            props.onHide();
          }}
          onComplete={isEditing() ? handleEditDevice : handleAddDevice}
          loading={mutationLoading}
        />
      )}
      
      {showRosteredEdit && (
        <RosteredDeviceDialog
          deviceUid={getSelectedDeviceUid() || ""}
          name={userProfileScreen.identification.fullName}
          onRequestHide={() => {
            setShowRosteredEdit(false);
            props.onHide();
          }}
          onComplete={() => {
            setShowRosteredEdit(false);
            props.onHide();
          }}
        />
      )}

      {dataContactInfo && (
        <ContactMergeDialog
          name={userProfileScreen.identification.fullName}
          loading={mutationLoading}
          deviceUid={dataContactInfo.deviceUid}
          isRostered={dataContactInfo.isRostered}
          owner={{
            avatar: dataContactInfo.contactInfoForDevice?.user?.avatar ?? undefined,
            initials: dataContactInfo.contactInfoForDevice?.user?.initials ?? '',
            display_name: dataContactInfo.contactInfoForDevice?.user?.display_name ?? '',
            groupNames: parseGroupNames(
              dataContactInfo.contactInfoForDevice?.groupNames
                ?.map(g => g.class_name ?? '')
                ?.filter(name => name.length > 0) ?? []
            ),
          }}
          onRequestHide={() => setDataContactInfo(null)}
          onRequestBack={() => {
            setDataContactInfo(null);
            if (props.selectedDevice) {
              setShowEdit(true);
            } else {
              setShowAdd(true);
            }
          }}
          onRemoveRequest={async () => {
            const result = await submitAddDevice({
              deviceUid: dataContactInfo.deviceUid,
              confirmAction: AdminAddContactInfoConfirmationActionEnum.steal,
              action: 'removed',
            });

            if (result?.data?.api_adminAddContactInfoToUser?.errors) {
              props.onError(result.data.api_adminAddContactInfoToUser.errors[0].message);
            }
          }}
          onMergeRequest={async () => {
            const result = await submitAddDevice({
              deviceUid: dataContactInfo.deviceUid,
              confirmAction: AdminAddContactInfoConfirmationActionEnum.merge,
              action: 'merged',
            });

            if (result?.data?.api_adminAddContactInfoToUser?.errors) {
              props.onError(result.data.api_adminAddContactInfoToUser.errors[0].message);
            }
          }}
        />
      )}
      <Link weight="semibold" onClick={handleAddContactInfo}>Add contact information</Link>
    </>
  );
};

const ContactInfoCompose = compose(
  graphql(USER_PROFILE_SCREEN, {
    name: 'userProfileScreen',
    options: (props: any) => ({
      variables: {
        input: {
          relatedUserUuid: props.relatedUserUuid,
          organizationUuid: props.organizationUuid,
        },
      },
    }),
  }),
  graphql(API_CONTACT_INFO_FOR_USER, {
    name: 'contactInfoForUser',
    options: (props: any) => ({
      variables: {
        targetUserUuid: props.relatedUserUuid,
      },
      fetchPolicy: 'network-only',
    }),
  }),
  graphql(API_ADMIN_ADD_CONTACT_INFO_TO_USER, { name: 'addContactInfo' }),
)(ContactInfo) as typeof ContactInfo;

export default ContactInfoCompose;
